import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { shipmentsAPI, suppliersAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  Ship, LogOut, LayoutDashboard, Package, Users,
  ArrowLeft, Loader2, CalendarIcon, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cn } from '../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

function Sidebar({ onLogout }) {
  return (
    <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-slate-200 p-6">
      <Link to="/dashboard" className="flex items-center gap-2 mb-8">
        <div className="w-8 h-8 bg-importdocs-primary rounded-md flex items-center justify-center">
          <Ship className="w-5 h-5 text-white" />
        </div>
        <span className="font-heading font-bold text-xl text-importdocs-text">ImportDocs</span>
      </Link>
      
      <nav className="flex-1 space-y-1">
        <Link
          to="/dashboard"
          className="flex items-center gap-3 px-4 py-3 rounded-md text-importdocs-text-secondary hover:bg-slate-100"
        >
          <LayoutDashboard className="w-5 h-5" />
          Dashboard
        </Link>
        <Link
          to="/dashboard/shipments"
          className="flex items-center gap-3 px-4 py-3 rounded-md bg-importdocs-primary text-white"
        >
          <Package className="w-5 h-5" />
          Shipments
        </Link>
        <Link
          to="/dashboard/suppliers"
          className="flex items-center gap-3 px-4 py-3 rounded-md text-importdocs-text-secondary hover:bg-slate-100"
        >
          <Users className="w-5 h-5" />
          Suppliers
        </Link>
      </nav>
      
      <Button 
        variant="ghost" 
        className="justify-start text-importdocs-text-secondary hover:text-importdocs-danger"
        onClick={onLogout}
      >
        <LogOut className="w-5 h-5 mr-2" />
        Logout
      </Button>
    </aside>
  );
}

function AddSupplierDialog({ open, onOpenChange, onSupplierAdded }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    country: '',
    contact_name: '',
    email: '',
    phone: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.country) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setLoading(true);
    try {
      const res = await suppliersAPI.create(formData);
      toast.success('Supplier added successfully');
      onSupplierAdded(res.data);
      onOpenChange(false);
      setFormData({ name: '', country: '', contact_name: '', email: '', phone: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add supplier');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add New Supplier</DialogTitle>
          <DialogDescription>
            Create a new supplier to use in this shipment
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="supplier-name">Company Name *</Label>
            <Input
              id="supplier-name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., ABC Manufacturing Co."
              data-testid="new-supplier-name-input"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="supplier-country">Country *</Label>
            <Input
              id="supplier-country"
              value={formData.country}
              onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
              placeholder="e.g., China"
              data-testid="new-supplier-country-input"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="supplier-contact">Contact Name</Label>
              <Input
                id="supplier-contact"
                value={formData.contact_name}
                onChange={(e) => setFormData(prev => ({ ...prev, contact_name: e.target.value }))}
                placeholder="John Doe"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="supplier-phone">Phone</Label>
              <Input
                id="supplier-phone"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+1 234 567"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="supplier-email">Email</Label>
            <Input
              id="supplier-email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="supplier@company.com"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-importdocs-primary hover:bg-importdocs-primary-dark"
              disabled={loading}
              data-testid="save-new-supplier-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Supplier'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function CreateShipmentPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(true);
  const [addSupplierDialogOpen, setAddSupplierDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    reference: '',
    supplier_id: '',
    supplier_name: '',
    country_of_origin: '',
    transport_type: '',
    estimated_fob: '',
    estimated_freight: '',
    estimated_insurance: '',
    estimated_duties: '',
  });

  useEffect(() => {
    loadSuppliers();
  }, []);

  const loadSuppliers = async () => {
    try {
      const res = await suppliersAPI.getAll();
      setSuppliers(res.data);
    } catch (error) {
      console.error('Failed to load suppliers');
    } finally {
      setLoadingSuppliers(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSupplierSelect = (supplierId) => {
    if (supplierId === 'add_new') {
      setAddSupplierDialogOpen(true);
      return;
    }
    
    const supplier = suppliers.find(s => s.id === supplierId);
    if (supplier) {
      setFormData(prev => ({
        ...prev,
        supplier_id: supplier.id,
        supplier_name: supplier.name,
        country_of_origin: supplier.country,
      }));
    }
  };

  const handleSupplierAdded = (newSupplier) => {
    setSuppliers(prev => [...prev, newSupplier]);
    setFormData(prev => ({
      ...prev,
      supplier_id: newSupplier.id,
      supplier_name: newSupplier.name,
      country_of_origin: newSupplier.country,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.supplier_name || !formData.country_of_origin || !formData.transport_type) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        ...formData,
        estimated_arrival: date ? format(date, 'yyyy-MM-dd') : null,
        estimated_fob: parseFloat(formData.estimated_fob) || 0,
        estimated_freight: parseFloat(formData.estimated_freight) || 0,
        estimated_insurance: parseFloat(formData.estimated_insurance) || 0,
        estimated_duties: parseFloat(formData.estimated_duties) || 0,
      };
      
      const res = await shipmentsAPI.create(payload);
      toast.success('Shipment created successfully');
      navigate(`/dashboard/shipments/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create shipment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-importdocs-bg flex">
      <Sidebar onLogout={handleLogout} />
      
      <div className="flex-1 flex flex-col">
        <header className="bg-white border-b border-slate-200 px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/dashboard/shipments">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <h1 className="font-heading font-bold text-xl text-importdocs-text">Create New Shipment</h1>
          </div>
        </header>
        
        <main className="flex-1 p-6">
          <form onSubmit={handleSubmit}>
            <div className="max-w-3xl mx-auto space-y-6">
              {/* Basic Info */}
              <Card className="border-slate-200" data-testid="basic-info-card">
                <CardHeader>
                  <CardTitle className="font-heading">Basic Information</CardTitle>
                  <CardDescription>Enter the shipment details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Shipment Name *</Label>
                      <Input
                        id="name"
                        placeholder="e.g., Electronics from Shenzhen"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        className="border-slate-200"
                        data-testid="shipment-name-input"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reference">Reference (Optional)</Label>
                      <Input
                        id="reference"
                        placeholder="Your internal reference"
                        value={formData.reference}
                        onChange={(e) => handleChange('reference', e.target.value)}
                        className="border-slate-200"
                        data-testid="shipment-reference-input"
                      />
                    </div>
                  </div>
                  
                  {/* Supplier Selection */}
                  <div className="space-y-2">
                    <Label>Supplier *</Label>
                    <Select 
                      value={formData.supplier_id} 
                      onValueChange={handleSupplierSelect}
                    >
                      <SelectTrigger className="border-slate-200" data-testid="supplier-select">
                        <SelectValue placeholder={loadingSuppliers ? "Loading suppliers..." : "Select a supplier"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="add_new" className="text-importdocs-primary font-medium">
                          <div className="flex items-center gap-2">
                            <Plus className="w-4 h-4" />
                            Add New Supplier
                          </div>
                        </SelectItem>
                        {suppliers.length > 0 && (
                          <div className="border-t border-slate-100 my-1" />
                        )}
                        {suppliers.map((supplier) => (
                          <SelectItem key={supplier.id} value={supplier.id}>
                            {supplier.name} ({supplier.country})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formData.supplier_id && !formData.supplier_id.startsWith('add') && (
                      <p className="text-xs text-importdocs-text-muted">
                        Country will be auto-filled from supplier
                      </p>
                    )}
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="supplier">Supplier Name *</Label>
                      <Input
                        id="supplier"
                        placeholder="e.g., ABC Manufacturing Co."
                        value={formData.supplier_name}
                        onChange={(e) => handleChange('supplier_name', e.target.value)}
                        className="border-slate-200"
                        data-testid="supplier-name-input"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="country">Country of Origin *</Label>
                      <Input
                        id="country"
                        placeholder="e.g., China"
                        value={formData.country_of_origin}
                        onChange={(e) => handleChange('country_of_origin', e.target.value)}
                        className="border-slate-200"
                        data-testid="country-input"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Transport Type *</Label>
                      <Select 
                        value={formData.transport_type} 
                        onValueChange={(v) => handleChange('transport_type', v)}
                      >
                        <SelectTrigger className="border-slate-200" data-testid="transport-type-select">
                          <SelectValue placeholder="Select transport type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Sea">Sea</SelectItem>
                          <SelectItem value="Air">Air</SelectItem>
                          <SelectItem value="Road">Road</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Estimated Arrival Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal border-slate-200",
                              !date && "text-muted-foreground"
                            )}
                            data-testid="eta-date-btn"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {date ? format(date, "PPP") : "Pick a date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={date}
                            onSelect={setDate}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Cost Estimates */}
              <Card className="border-slate-200" data-testid="cost-estimates-card">
                <CardHeader>
                  <CardTitle className="font-heading">Cost Estimates</CardTitle>
                  <CardDescription>Enter estimated costs (in USD). These will be compared against actual costs later.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="fob">FOB (Free on Board)</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-importdocs-text-muted">$</span>
                        <Input
                          id="fob"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          value={formData.estimated_fob}
                          onChange={(e) => handleChange('estimated_fob', e.target.value)}
                          className="pl-8 border-slate-200"
                          data-testid="fob-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="freight">Freight</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-importdocs-text-muted">$</span>
                        <Input
                          id="freight"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          value={formData.estimated_freight}
                          onChange={(e) => handleChange('estimated_freight', e.target.value)}
                          className="pl-8 border-slate-200"
                          data-testid="freight-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="insurance">Insurance</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-importdocs-text-muted">$</span>
                        <Input
                          id="insurance"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          value={formData.estimated_insurance}
                          onChange={(e) => handleChange('estimated_insurance', e.target.value)}
                          className="pl-8 border-slate-200"
                          data-testid="insurance-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="duties">Duties / GST</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-importdocs-text-muted">$</span>
                        <Input
                          id="duties"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          value={formData.estimated_duties}
                          onChange={(e) => handleChange('estimated_duties', e.target.value)}
                          className="pl-8 border-slate-200"
                          data-testid="duties-input"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Actions */}
              <div className="flex justify-end gap-4">
                <Link to="/dashboard/shipments">
                  <Button variant="outline" type="button">
                    Cancel
                  </Button>
                </Link>
                <Button 
                  type="submit" 
                  className="bg-importdocs-primary hover:bg-importdocs-primary-dark"
                  disabled={loading}
                  data-testid="create-shipment-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create Shipment'
                  )}
                </Button>
              </div>
            </div>
          </form>
        </main>
      </div>
      
      <AddSupplierDialog 
        open={addSupplierDialogOpen}
        onOpenChange={setAddSupplierDialogOpen}
        onSupplierAdded={handleSupplierAdded}
      />
    </div>
  );
}
